<template>
    <section :id="`element-${settings.id}`" ref="$gallery" :class="[classes]" data-is-hero>
        <div class="elle-scroll-gallery--wrapper" :style="{ backgroundColor: settings?.color }">
            <div :class="[grid]">
                <div class="l-col">
                    <UiHeadline :settings="{ label: activeItem.label, headline: activeItem.headline }" />
                    <div class="indicator">{{ zeroPad(activeIndex + 1, 2) }} / {{ zeroPad(settings?.stepItems.length, 2) }}</div>
                </div>
                <div class="r-col">
                    <div class="scroll-container">
                        <div ref="$sequence" class="image-sequence">
                            <div v-for="item in settings?.stepItems" ref="$medias" :key="item.id" class="page-media w-ratio">
                                <UiImage :src="item.media[0].webp" :classes="'responsive full'" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { CmsItem } from '~/types/cms';

const props = withDefaults(
    defineProps<{
        settings?: CmsItem;
    }>(),
    {
        settings: () => ({})
    }
);

const { $gsap: gsap, $ScrollTrigger: ScrollTrigger } = useNuxtApp();

const $gallery = ref<HTMLDivElement | null>(null);
const $sequence = ref<HTMLDivElement | null>(null);

const $medias = ref<HTMLElement[]>([]);
const activeIndex = ref(0);
const settings = computed(() => props.settings);

const classes = computed(() => {
    const classes = [];
    classes.push('elle-scroll-gallery');

    if (settings.value?.wrapped) classes.push('wrapped');
    if (settings.value?.fullScreen) classes.push('f-screen');

    return classes.join(' ');
});

const grid = computed(() => (settings.value?.wrapped ? 'grid12 center-v' : 'grid12'));
const activeItem = computed(() => settings.value?.stepItems?.[activeIndex.value] ?? {});

useStartAnimations($gallery.value!, () => {
    const itemLength = settings.value?.stepItems?.length ?? 0;
    if (!$sequence.value || !itemLength) return;

    ScrollTrigger?.matchMedia({
        '(max-width: 767px)': () => {
            const offset = 50;
            const itemWidth = ($sequence.value!.clientWidth / itemLength) * (itemLength - 1);
            gsap.to($sequence.value!, {
                x: -itemWidth + offset,
                ease: 'none',
                scrollTrigger: {
                    invalidateOnRefresh: true,
                    trigger: $gallery.value,
                    start: 'top -10%',
                    end: `+=${itemWidth - offset * 2}px`,
                    pin: true,
                    scrub: true,
                    snap: {
                        snapTo: 1 / (itemLength - 1),
                        duration: { min: 0.2, max: 0.6 },
                        ease: 'power4'
                    },
                    onUpdate: self => {
                        const index = Math.round(self.progress * (itemLength - 1));
                        if (index !== activeIndex.value) {
                            activeIndex.value = index;
                        }
                    }
                }
            });
        },
        '(min-width: 768px)': () => {
            const sequenceHeightFull = $sequence.value!.clientHeight;
            const sequenceHeight = (sequenceHeightFull / itemLength) * (itemLength - 1);
            const itemsHeight = $medias.value.map(item => item.clientHeight).reduce((a, i) => a + i + 50, 0);
            const offset = settings.value?.wrapped ? -50 : sequenceHeightFull - itemsHeight;

            const start = settings.value?.wrapped ? 'top 5%' : 'top top';

            gsap.to($sequence.value!, {
                y: -sequenceHeight + offset,
                ease: 'none',
                scrollTrigger: {
                    trigger: $gallery.value,
                    invalidateOnRefresh: true,
                    start: start,
                    end: `+=${sequenceHeight - offset}px`,
                    pin: true,
                    scrub: true,
                    snap: {
                        snapTo: 1 / (itemLength - 1),
                        duration: { min: 0.2, max: 0.6 },
                        ease: 'power4'
                    },
                    onUpdate: self => {
                        const index = Math.round(self.progress * (itemLength - 1));
                        if (index !== activeIndex.value) {
                            activeIndex.value = index;
                        }
                    }
                }
            });
        }
    });
});
</script>
